<template>
  <div
    class="container-fluid pm-page dashboard-page"
    :class="{ 'dashboard-page--single': !isAllTeamsView }"
  >
    <div class="pm-page-header dashboard-page-header d-flex align-items-center justify-content-between mb-4">
      <div>
        <h5 class="mb-0 font-weight-bold">Dashboard</h5>
        <p class="text-secondary text-sm mb-0">
          Plan task progress by team — latest active sprint
        </p>
      </div>
      <div class="dashboard-team-filter">
        <base-select
          size="sm"
          :model-value="selectedTeamFilter"
          :options="teamFilterOptions"
          option-label="label"
          option-value="value"
          option-disabled="disabled"
          @update:model-value="onTeamFilterChange"
        />
      </div>
    </div>

    <div v-if="error" class="alert alert-danger text-sm py-2" role="alert">
      {{ error }}
    </div>

    <div v-if="loading && !teams.length" class="text-secondary text-sm py-4">
      Loading team sprint summary...
    </div>

    <div v-else-if="isAllTeamsView" class="row g-4">
      <div
        v-for="team in visibleTeams"
        :key="team.team"
        class="col-lg-4"
      >
        <team-sprint-summary-card v-bind="teamCardProps(team)" />
      </div>
    </div>

    <div v-else class="row g-4 dashboard-page-main">
      <div class="col-lg-4 dashboard-page-col">
        <team-sprint-summary-card
          v-if="selectedTeamData"
          v-bind="teamCardProps(selectedTeamData)"
        />
      </div>
      <div class="col-lg-8 dashboard-page-col">
        <team-sprint-detail-panel
          :team="selectedTeamFilter"
          :loading="calendarLoading"
          :error="calendarError"
          :start-date="calendarData.startDate"
          :end-date="calendarData.endDate"
          :week-count="calendarData.weekCount"
          :tasks="calendarData.tasks"
          :milestones="calendarData.milestones"
          :overdue-tasks="calendarData.overdueTasks"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from "vuex";
import BaseSelect from "@/components/BaseSelect.vue";
import TeamSprintSummaryCard from "./components/TeamSprintSummaryCard.vue";
import TeamSprintDetailPanel from "./components/TeamSprintDetailPanel.vue";
import { dashboardApi } from "@/services/backendApi";
import {
  buildDashboardTeamFilterOptions,
  canViewAllDashboardTeams,
  dashboardAssignableTeamCodes,
  defaultDashboardTeamFilter,
  isDashboardTeamFilterAllowed,
} from "@/utils/userTeams";
import "@/assets/css/pm-tab-scroll.css";

const EMPTY_TEAM = (team, teamLabel) => ({
  team,
  teamLabel,
  sprintId: null,
  sprintName: null,
  startDate: null,
  endDate: null,
  doneCount: 0,
  totalCount: 0,
  inProgressPercent: 0,
  readyToDeployCount: 0,
  waitingToConfirmCount: 0,
  toDoCount: 0,
  doneStatusCount: 0,
  inProgressStatusCount: 0,
  testingCount: 0,
  bugCount: 0,
});

const EMPTY_TEAMS = [
  EMPTY_TEAM("a", "Team A"),
  EMPTY_TEAM("b", "Team B"),
  EMPTY_TEAM("c", "Team C"),
];

const EMPTY_CALENDAR = {
  sprintName: null,
  startDate: null,
  endDate: null,
  weekCount: null,
  tasks: [],
  milestones: [],
  overdueTasks: [],
};

export default {
  name: "dashboard-default",
  components: { BaseSelect, TeamSprintSummaryCard, TeamSprintDetailPanel },
  data() {
    return {
      loading: false,
      error: null,
      teams: [...EMPTY_TEAMS],
      selectedTeamFilter: "all",
      calendarLoading: false,
      calendarError: null,
      calendarData: { ...EMPTY_CALENDAR },
    };
  },
  computed: {
    ...mapState(["user"]),
    teamFilterOptions() {
      return buildDashboardTeamFilterOptions(this.user);
    },
    canViewAllTeams() {
      return canViewAllDashboardTeams(this.user);
    },
    assignableTeamCodes() {
      return dashboardAssignableTeamCodes(this.user);
    },
    isAllTeamsView() {
      return this.selectedTeamFilter === "all";
    },
    visibleTeams() {
      if (!this.isAllTeamsView) return [];
      if (this.canViewAllTeams) return this.teams;
      const allowed = new Set(this.assignableTeamCodes);
      return this.teams.filter((team) => allowed.has(team.team));
    },
    selectedTeamData() {
      return this.teams.find((team) => team.team === this.selectedTeamFilter) || null;
    },
  },
  watch: {
    user: {
      deep: true,
      handler() {
        this.syncTeamFilterFromUser();
      },
    },
    isAllTeamsView: {
      handler() {
        this.syncScrollLock();
      },
    },
  },
  mounted() {
    this.syncScrollLock();
  },
  beforeUnmount() {
    document.documentElement.classList.remove("pm-scroll-lock");
  },
  async created() {
    this.syncTeamFilterFromUser();
    await this.loadSummary();
    await this.loadCalendarIfNeeded();
  },
  methods: {
    syncScrollLock() {
      document.documentElement.classList.toggle("pm-scroll-lock", !this.isAllTeamsView);
    },
    syncTeamFilterFromUser() {
      if (!isDashboardTeamFilterAllowed(this.user, this.selectedTeamFilter)) {
        this.selectedTeamFilter = defaultDashboardTeamFilter(this.user);
        this.loadCalendarIfNeeded();
      }
    },
    onTeamFilterChange(value) {
      if (!isDashboardTeamFilterAllowed(this.user, value)) return;
      this.selectedTeamFilter = value;
      this.loadCalendarIfNeeded();
    },
    teamCardProps(team) {
      return {
        team: team.team,
        teamLabel: team.teamLabel,
        startDate: team.startDate,
        endDate: team.endDate,
        doneCount: team.doneCount,
        totalCount: team.totalCount,
        inProgressPercent: team.inProgressPercent,
        readyToDeployCount: team.readyToDeployCount,
        waitingToConfirmCount: team.waitingToConfirmCount,
        toDoCount: team.toDoCount,
        doneStatusCount: team.doneStatusCount,
        inProgressStatusCount: team.inProgressStatusCount,
        testingCount: team.testingCount,
        bugCount: team.bugCount,
      };
    },
    async loadSummary() {
      this.loading = true;
      this.error = null;
      try {
        const data = await dashboardApi.getTeamSprintSummary();
        this.teams = Array.isArray(data?.teams) && data.teams.length
          ? data.teams
          : [...EMPTY_TEAMS];
      } catch (e) {
        console.error("Dashboard loadSummary error:", e);
        this.error = e.message || "Failed to load dashboard data";
        this.teams = [...EMPTY_TEAMS];
      } finally {
        this.loading = false;
      }
    },
    async loadCalendarIfNeeded() {
      if (this.isAllTeamsView) {
        this.calendarError = null;
        this.calendarData = { ...EMPTY_CALENDAR };
        return;
      }

      this.calendarLoading = true;
      this.calendarError = null;
      try {
        const data = await dashboardApi.getTeamSprintCalendar(this.selectedTeamFilter);
        this.calendarData = {
          sprintName: data?.sprintName || null,
          startDate: data?.startDate || null,
          endDate: data?.endDate || null,
          weekCount: data?.weekCount != null ? Number(data.weekCount) : null,
          tasks: Array.isArray(data?.tasks) ? data.tasks : [],
          milestones: Array.isArray(data?.milestones) ? data.milestones : [],
          overdueTasks: Array.isArray(data?.overdueTasks) ? data.overdueTasks : [],
        };
      } catch (e) {
        console.error("Dashboard loadCalendar error:", e);
        this.calendarError = e.message || "Failed to load sprint calendar";
        this.calendarData = { ...EMPTY_CALENDAR };
      } finally {
        this.calendarLoading = false;
      }
    },
  },
};
</script>

<style scoped>
.dashboard-page-header {
  flex-shrink: 0;
}

.dashboard-page {
  overflow-x: hidden;
  overflow-y: auto;
}

.dashboard-page--single {
  height: 100%;
  max-height: 100%;
}

.dashboard-page-main {
  flex: 1 0 auto;
  min-height: 0;
  align-items: stretch;
}

.dashboard-page-col {
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.dashboard-team-filter {
  min-width: 10.5rem;
}

.dashboard-team-filter :deep(.base-select) {
  width: 100%;
}
</style>

<style>
html.pm-scroll-lock .page-view .dashboard-page.pm-page,
html.pm-scroll-lock .main-content .dashboard-page.pm-page {
  overflow-x: hidden;
  overflow-y: auto;
}
</style>
